import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { enhancedServices } from "../../services/enhancedCrudService";

const SanitarywareTypes = () => {
  const [sanitarywareTypes, setSanitarywareTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeCount, setTypeCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: "" });
  const [deletingType, setDeletingType] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [forceSoftDelete, setForceSoftDelete] = useState(false);
  const [undoDeprecationModal, setUndoDeprecationModal] = useState(false);
  const [undoDeprecatingType, setUndoDeprecatingType] = useState(null);
  const [assignAttributesModal, setAssignAttributesModal] = useState(false);
  const [assigningType, setAssigningType] = useState(null);
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);
  const [assigningAttributes, setAssigningAttributes] = useState(false);
  const [typeFilters, setTypeFilters] = useState([]);
  const [loadingTypeFilters, setLoadingTypeFilters] = useState(false);
  const [warningModal, setWarningModal] = useState(false);

  const fetchSanitarywareTypes = async () => {
    setLoading(true);
    try {
      const response = await enhancedServices.sanitaryware.getTypes();
      if (response.types) {
        setSanitarywareTypes(response.types || []);
        setTypeCount(response.count || 0);
      } else {
        toast.error(response.message || "Failed to fetch sanitaryware types");
      }
    } catch (error) {
      console.error("Error fetching sanitaryware types:", error);
      toast.error("Failed to fetch sanitaryware types");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const openAddModal = () => {
    setModalMode("add");
    setFormData({ name: "" });
    setEditingType(null);
    setShowModal(true);
  };

  const openEditModal = (type) => {
    setModalMode("edit");
    setFormData({ name: type.name });
    setEditingType(type);
    setShowModal(true);
  };

  const openDeleteModal = (type) => {
    setDeletingType(type);
    setDeleteModal(true);
  };

  const openUndoDeprecationModal = (type) => {
    setUndoDeprecatingType(type);
    setUndoDeprecationModal(true);
  };

  const openAssignAttributesModal = async (type) => {
    setAssigningType(type);
    setSelectedAttributeIds([]);
    setAssignAttributesModal(true);

    // Fetch available attributes and type filters
    await Promise.all([fetchAvailableAttributes(), fetchTypeFilters(type.id)]);
  };

  const fetchTypeFilters = async (typeId) => {
    setLoadingTypeFilters(true);
    try {
      const response = await enhancedServices.sanitaryware.getTypeFilters(
        typeId
      );
      if (response && response.filters) {
        setTypeFilters(response.filters);
      } else {
        setTypeFilters([]);
      }
    } catch (error) {
      console.error("Error fetching type filters:", error);
      setTypeFilters([]);
    } finally {
      setLoadingTypeFilters(false);
    }
  };

  const fetchAvailableAttributes = async () => {
    setLoadingAttributes(true);
    try {
      const response = await enhancedServices.sanitaryware.getAttributes();
      if (response.attributes) {
        // Filter out deprecated attributes
        let activeAttributes = response.attributes.filter(
          (attr) => !attr.is_deprecated
        );

        setAvailableAttributes(activeAttributes);
      } else {
        toast.error(response.message || "Failed to fetch attributes");
      }
    } catch (error) {
      console.error("Error fetching attributes:", error);
      toast.error("Failed to fetch attributes");
    } finally {
      setLoadingAttributes(false);
    }
  };

  const handleAttributeSelection = (attributeId) => {
    setSelectedAttributeIds((prev) => {
      if (prev.includes(attributeId)) {
        return prev.filter((id) => id !== attributeId);
      } else {
        return [...prev, attributeId];
      }
    });
  };

  const selectAllAttributes = () => {
    const selectableAttributes = availableAttributes.filter((attr) => {
      const isAlreadyAssigned =
        typeFilters &&
        typeFilters.some(
          (assignedAttr) => assignedAttr.attribute_id === attr.id
        );
      return !isAlreadyAssigned;
    });
    setSelectedAttributeIds(selectableAttributes.map((attr) => attr.id));
  };

  const deselectAllAttributes = () => {
    setSelectedAttributeIds([]);
  };

  const handleAssignAttributes = async () => {
    if (selectedAttributeIds.length === 0) {
      toast.error("Please select at least one attribute");
      return;
    }

    if (assigningAttributes) {
      return; // Prevent multiple submissions
    }

    setAssigningAttributes(true);
    try {
      const response =
        await enhancedServices.sanitaryware.assignMultipleAttributes(
          assigningType.id,
          selectedAttributeIds
        );

      if (response) {
        // Show success message
        if (response.message) {
          toast.success(response.message);
        } else {
          const selectedAttributes = availableAttributes.filter((attr) =>
            selectedAttributeIds.includes(attr.id)
          );
          const attributeNames = selectedAttributes
            .map((attr) => attr.display_name || attr.name)
            .join(", ");
          toast.success(
            `Successfully assigned ${selectedAttributeIds.length} attribute(s) to "${assigningType?.name}": ${attributeNames}`,
            { autoClose: 5000 }
          );
        }

        setAssignAttributesModal(false);
        setAssigningType(null);
        setSelectedAttributeIds([]);
        // Refresh the types list to show updated data
        fetchSanitarywareTypes();
      } else {
        toast.error(response?.message || "Failed to assign attributes");
      }
    } catch (error) {
      console.error("Error assigning attributes:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to assign attributes. Please try again.");
      }
    } finally {
      setAssigningAttributes(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    try {
      if (modalMode === "add") {
        const response = await enhancedServices.sanitaryware.createType(
          formData
        );
        if (response.id) {
          toast.success(
            response.message || "Sanitaryware type created successfully"
          );
          setShowModal(false);
          fetchSanitarywareTypes();
        } else {
          toast.error(response.message || "Failed to create sanitaryware type");
        }
      } else {
        const response = await enhancedServices.sanitaryware.updateType(
          editingType.id,
          formData
        );
        if (response) {
          toast.success(
            response.message || "Sanitaryware type updated successfully"
          );
          setShowModal(false);
          fetchSanitarywareTypes();
        } else {
          toast.error(response.message || "Failed to update sanitaryware type");
        }
      }
    } catch (error) {
      console.error("Error saving sanitaryware type:", error);
      toast.error("Failed to save sanitaryware type");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingType) return;
    try {
      const response = await enhancedServices.sanitaryware.deleteType(
        deletingType.id
      );
      if (
        response &&
        response.linked_product_ids &&
        response.linked_product_ids.length > 0
      ) {
        setLinkedProducts(response.linked_product_ids);
        setWarningModal(true);
        setDeleteModal(false);
      } else {
        toast.success(
          response.message || "Sanitaryware type deleted successfully"
        );
        setDeleteModal(false);
        setDeletingType(null);
        fetchSanitarywareTypes();
      }
    } catch (error) {
      console.error("Error deleting sanitaryware type:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete sanitaryware type");
      }
      setDeleteModal(false);
      setDeletingType(null);
    }
  };

  const handleDeleteWithForceSoftDelete = async () => {
    if (!deletingType) return;
    try {
      const deleteData = {
        force_soft_delete: true,
      };
      const response = await enhancedServices.sanitaryware.deleteType(
        deletingType.id,
        deleteData
      );
      if (response) {
        if (response.status === "soft_deleted") {
          toast.success(
            response.message || "Type marked as deprecated successfully"
          );
        } else if (response.status === "deleted") {
          toast.success(
            response.message || "Sanitaryware type deleted successfully"
          );
        } else {
          toast.success(
            response.message || "Sanitaryware type deleted successfully"
          );
        }
        setWarningModal(false);
        setDeletingType(null);
        setLinkedProducts([]);
        setForceSoftDelete(false);
        fetchSanitarywareTypes();
      } else {
        toast.error(response.message || "Failed to delete sanitaryware type");
      }
    } catch (error) {
      console.error("Error deleting sanitaryware type:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete sanitaryware type");
      }
    }
  };

  const handleUndoDeprecation = async () => {
    if (!undoDeprecatingType) return;
    try {
      const response = await enhancedServices.sanitaryware.undoDeprecationType(
        undoDeprecatingType.id
      );
      if (response) {
        toast.success(response.message || "Deprecation undone successfully");
        setUndoDeprecationModal(false);
        setUndoDeprecatingType(null);
        fetchSanitarywareTypes();
      } else {
        toast.error(response.message || "Failed to undo deprecation");
      }
    } catch (error) {
      console.error("Error undoing deprecation:", error);
      toast.error("Failed to undo deprecation");
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ name: "" });
    setEditingType(null);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletingType(null);
    setLinkedProducts([]);
    setForceSoftDelete(false);
  };

  const closeUndoDeprecationModal = () => {
    setUndoDeprecationModal(false);
    setUndoDeprecatingType(null);
  };

  const closeWarningModal = () => {
    setWarningModal(false);
    setDeletingType(null);
    setLinkedProducts([]);
    setForceSoftDelete(false);
  };

  const closeAssignAttributesModal = () => {
    setAssignAttributesModal(false);
    setAssigningType(null);
    setSelectedAttributeIds([]);
    setAvailableAttributes([]);
    setTypeFilters([]);
  };

  useEffect(() => {
    fetchSanitarywareTypes();
  }, []);

  return (
    <>
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">Sanitaryware Types</h3>
            <p className="text-muted mb-0">
              Manage sanitaryware types and their associated attributes.
            </p>
          </div>
          <div className="col-auto text-end float-end ms-auto">
            <button className="btn btn-primary" onClick={openAddModal}>
              <i className="fe fe-plus"></i> Add New Type
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Sanitaryware</h5>
              <span className="badge bg-primary">Total: {typeCount}</span>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="text-muted mt-3">
                    Loading sanitaryware types...
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead>
                      <tr>
                        <th>Sr.No</th>
                        <th>Name</th>
                        <th>Deprecated</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sanitarywareTypes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center py-5">
                            <i className="fe fe-package fe-3x text-muted mb-3 d-block"></i>
                            <p className="text-muted mb-2">
                              No sanitaryware types found
                            </p>
                            <small className="text-muted">
                              Get started by creating your first sanitaryware
                              type
                            </small>
                          </td>
                        </tr>
                      ) : (
                        sanitarywareTypes.map((type,index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{type.name}</td>
                            <td>{type.is_deprecated ? "Yes" : "No"}</td>
                            <td>
                              <div className="actions d-flex flex-wrap gap-1">
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => openEditModal(type)}
                                  title="Edit"
                                >
                                  <i className="fe fe-edit"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() =>
                                    openAssignAttributesModal(type)
                                  }
                                  title="Assign Attributes"
                                >
                                  <i className="fe fe-tag"></i>
                                </button>
                                {type.is_deprecated && (
                                  <button
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() =>
                                      openUndoDeprecationModal(type)
                                    }
                                    title="Undo Deprecation"
                                  >
                                    <i className="fe fe-rotate-ccw"></i>
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => openDeleteModal(type)}
                                  title="Delete"
                                >
                                  <i className="fe fe-trash-2"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalMode === "add"
                    ? "Add New Sanitaryware Type"
                    : "Edit Sanitaryware Type"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter sanitaryware type name"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {modalMode === "add" ? "Create" : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      {deleteModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeDeleteModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to delete the sanitaryware type "
                  <strong>{deletingType?.name}</strong>"?
                </p>
                <p className="text-danger">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {warningModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm soft Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeWarningModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fe fe-alert-triangle me-2"></i>
                  <strong>Warning:</strong> The sanitaryware type "
                  <strong>{deletingType?.name}</strong>" is linked to{" "}
                  {linkedProducts.length} product(s).
                  <p className="text-danger">
                    Are you sure you want to soft delete the tile size "
                    <strong>{deletingType?.name}</strong>"?
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeWarningModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteWithForceSoftDelete}
                >
                  Delete Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {undoDeprecationModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Undo Deprecation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeUndoDeprecationModal}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to undo the deprecation for "
                  <strong>{undoDeprecatingType?.name}</strong>"?
                </p>
                <p className="text-warning">
                  This will make the type active again and available for use.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeUndoDeprecationModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleUndoDeprecation}
                >
                  Undo Deprecation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {assignAttributesModal && (
        <div
          className="modal fade show assign-attributes-modal"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Assign Attributes to {assigningType?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAssignAttributesModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3">
                  <i className="fe fe-info me-2"></i>
                  Select the attributes you want to assign to this sanitaryware
                  type. These attributes will be available when creating or
                  editing products of this type.
                </div>

                {assigningType?.is_deprecated && (
                  <div className="alert alert-warning mb-3">
                    <i className="fe fe-alert-triangle me-2"></i>
                    <strong>Warning:</strong> This type is deprecated. Consider
                    undoing deprecation before assigning new attributes.
                  </div>
                )}

                <div className="mb-3">
                  <label htmlFor="availableAttributes" className="form-label">
                    Available Attributes ({availableAttributes.length})
                  </label>

                  {availableAttributes.length > 0 && (
                    <div className="mb-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm me-2"
                        onClick={selectAllAttributes}
                        disabled={loadingTypeFilters}
                      >
                        Select All Available
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={deselectAllAttributes}
                      >
                        Deselect All
                      </button>
                    </div>
                  )}

                  {loadingAttributes || loadingTypeFilters ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">
                          Loading attributes...
                        </span>
                      </div>
                      <p className="text-muted mt-2">
                        {loadingAttributes
                          ? "Loading available attributes..."
                          : "Loading type filters..."}
                      </p>
                    </div>
                  ) : (
                    <div className="list-group">
                      {availableAttributes.length === 0 ? (
                        <div className="text-center py-4">
                          <i className="fe fe-inbox fe-3x text-muted mb-3"></i>
                          <p className="text-muted mb-2">
                            No available attributes found.
                          </p>
                          <small className="text-muted">
                            There are no active attributes available for
                            assignment.
                          </small>
                        </div>
                      ) : (
                        availableAttributes.map((attr) => {
                          const isAlreadyAssigned =
                            typeFilters &&
                            typeFilters.some(
                              (assignedAttr) =>
                                assignedAttr.attribute_id === attr.id
                            );

                          return (
                            <div
                              key={attr.id}
                              className="list-group-item d-flex justify-content-between align-items-center"
                            >
                              <div>
                                <strong>
                                  {attr.display_name || attr.name}
                                </strong>
                                <br />
                                <small className="text-muted">
                                  {attr.name}{" "}
                                  {attr.is_required && (
                                    <span className="badge bg-warning">
                                      Required
                                    </span>
                                  )}
                                </small>
                              </div>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`attr-${attr.id}`}
                                checked={
                                  selectedAttributeIds.includes(attr.id) ||
                                  isAlreadyAssigned
                                }
                                onChange={() =>
                                  handleAttributeSelection(attr.id)
                                }
                                disabled={isAlreadyAssigned}
                                title={
                                  isAlreadyAssigned
                                    ? "This attribute is already assigned to this type"
                                    : ""
                                }
                              />
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <div className="me-auto">
                  {selectedAttributeIds.length > 0 && (
                    <span className="text-muted">
                      {selectedAttributeIds.length} attribute(s) selected
                    </span>
                  )}
                  {availableAttributes.length === 0 && (
                    <span className="text-muted">
                      No attributes available for assignment
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  onClick={closeAssignAttributesModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignAttributes}
                  disabled={
                    assigningAttributes ||
                    availableAttributes.length === 0 ||
                    selectedAttributeIds.length === 0
                  }
                >
                  {assigningAttributes ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Assigning...
                    </>
                  ) : (
                    "Assign Attributes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {(showModal ||
        deleteModal ||
        warningModal ||
        undoDeprecationModal ||
        assignAttributesModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default SanitarywareTypes;
